import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { YStack, XStack, Text, View, ScrollView } from "tamagui";
import Header from "@components/layout/Header";
import { colors } from "@styles/colors";
import { containerStyle } from "@styles/index";
import {
  enablePractice,
  exitPractice,
  getPracticeScenario,
  resolveScenarioSlug,
  PracticeScenario,
  PRACTICE_SCENARIOS,
  PRACTICE_CATEGORIES,
  PRACTICE_OTP,
  PRACTICE_FAMILY_INVITE_CODE,
} from "@utils/practice/practiceMode";
import { startScenario } from "./practiceFlow";
import MerchantInfo from "./MerchantInfo";

/**
 * 練習模式 Hub（路由 /practice）—— 客服練習的完整引導入口。
 * - 一般：顯示分類情境選單（歡迎說明 + 各情境卡），點卡片才啟動該情境。
 * - ?scenario=xxx：深連結，直接啟動指定情境（給書籤/教學連結用）。
 * - ?done=xxx：情境完成後導回，顯示完成畫面。
 * 教練面板 CoachPanel 於 App 全域掛載，情境進行中一步步引導（/practice 本頁不顯示）。
 */
const Pill = ({ k, v }: { k: string; v: string }) => (
  <XStack ai="center" gap={6} bg="#FFF7ED" borderColor="#FED7AA" borderWidth={1} borderRadius={999} px={10} py={5}>
    <Text fontSize={11.5} color="#9A3412">{k}</Text>
    <Text fontSize={13} fontWeight="800" color="#B45309" letterSpacing={1}>{v}</Text>
  </XStack>
);

const PracticeEntry = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const routeParams = useParams<{ scenario?: string }>();
  const done = params.get("done");
  // 深連結：/practice/<slug> 路徑，或 ?scenario=xxx 查詢字串（都支援）。
  const deepLink = resolveScenarioSlug(routeParams.scenario) ?? resolveScenarioSlug(params.get("scenario") || undefined);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    enablePractice(getPracticeScenario());
    if (done) return; // 完成頁：不導頁
    if (deepLink) {
      startScenario(deepLink, navigate); // 深連結：直接啟動
      return;
    }
    setReady(true); // 顯示 Hub 選單
    // 依 done/deepLink 變化重跑：從 ?done=xxx 完成頁按「回練習選單」導回 /practice 時，
    // 元件不會重新掛載，需在此重評估才會把 ready 設 true 顯示選單（否則卡在載入中）。
    // eslint-disable-next-line
  }, [done, deepLink]);

  const start = (key: PracticeScenario) => startScenario(key, navigate);

  // 完成畫面
  if (done) {
    return (
      <View {...containerStyle} height="100dvh" width="100vw" background={colors.bg}>
        <Header>
          <Text fontSize={20} fontWeight="700" color="#FFF">練習完成</Text>
        </Header>
        <YStack flex={1} ai="center" jc="center" gap={16} px={28}>
          <YStack ai="center" jc="center" width={72} height={72} borderRadius={36} bg="#10B981">
            <Text fontSize={38} fontWeight="900" color="#fff">✓</Text>
          </YStack>
          <Text fontSize={20} fontWeight="800" color={colors.grayDark}>這個情境練習完成！</Text>
          <Text fontSize={14} color={colors.gray} ta="center" maw={320} lineHeight={20}>
            正式環境此時會把客戶導回 LINE 或 App 首頁。這裡是練習沙盒，可回選單換其他情境再練。
          </Text>
          <XStack
            className="primary-bg-gradient" borderRadius={16} p={15} mt="$2" w="100%" ai="center" jc="center" cursor="pointer"
            onPress={() => navigate("/practice", { replace: true })}>
            <Text color="white" fontSize={16} fontWeight="bold">回練習選單</Text>
          </XStack>
        </YStack>
      </View>
    );
  }

  if (!ready) {
    return (
      <View {...containerStyle} height="100dvh" width="100vw" background={colors.bg} ai="center" jc="center">
        <Text fontSize={14} color={colors.gray}>載入練習情境中…</Text>
      </View>
    );
  }

  // Hub 選單
  return (
    <View {...containerStyle} height="100dvh" width="100vw" background={colors.bg}>
      <Header>
        <Text fontSize={20} fontWeight="700" color="#FFF">客服練習模式</Text>
      </Header>
      <ScrollView flex={1} showsVerticalScrollIndicator={false} contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
        {/* 歡迎/說明 */}
        <YStack bg="#fff" borderRadius={18} p={16} gap={10} borderWidth={1} borderColor={colors.border || "#E7ECF3"}>
          <XStack ai="center" gap={10}>
            <YStack ai="center" jc="center" width={40} height={40} borderRadius={12} bg="#FFF7ED">
              <Text fontSize={22}>🎓</Text>
            </YStack>
            <YStack flex={1}>
              <Text fontSize={17} fontWeight="900" color={colors.grayDark}>練習沙盒</Text>
              <Text fontSize={12.5} color={colors.gray} mt={1}>畫面與客戶完全一樣，但不碰真實 LINE / 後端 / 簡訊</Text>
            </YStack>
          </XStack>
          <Text fontSize={13} color={colors.grayDark} lineHeight={20}>
            選一個情境開始，畫面下方會出現<Text fontWeight="800" color={colors.primary}>教練面板</Text>，一步步用大字告訴你「這一步要按哪個鈕、客戶會看到什麼、你可以怎麼引導、常見問題」。
          </Text>
          <XStack gap={8} flexWrap="wrap">
            <Pill k="練習驗證碼" v={PRACTICE_OTP} />
            <Pill k="家庭邀請碼" v={PRACTICE_FAMILY_INVITE_CODE} />
          </XStack>
        </YStack>

        {/* 分類情境 */}
        {PRACTICE_CATEGORIES.map((cat) => {
          const items = PRACTICE_SCENARIOS.filter((s) => s.category === cat.key);
          if (items.length === 0) return null;
          return (
            <YStack key={cat.key} mt={20} gap={10}>
              <YStack>
                <Text fontSize={14} fontWeight="800" color={colors.grayDark}>{cat.label}</Text>
                <Text fontSize={12} color={colors.gray} mt={1}>{cat.desc}</Text>
              </YStack>
              {items.map((s) => (
                <XStack
                  key={s.key}
                  ai="center"
                  gap={13}
                  bg="#fff"
                  borderRadius={16}
                  p={14}
                  borderWidth={1}
                  borderColor={colors.border || "#E7ECF3"}
                  cursor="pointer"
                  pressStyle={{ opacity: 0.7, scale: 0.99 }}
                  onPress={() => start(s.key)}>
                  <YStack ai="center" jc="center" width={44} height={44} borderRadius={13} bg="#F1F5F9" flexShrink={0}>
                    <Text fontSize={22}>{s.icon}</Text>
                  </YStack>
                  <YStack flex={1} minWidth={0}>
                    <Text fontSize={15.5} fontWeight="800" color={colors.grayDark}>{s.label}</Text>
                    <Text fontSize={12.5} color={colors.gray} mt={2} lineHeight={18}>{s.summary}</Text>
                  </YStack>
                  <Text fontSize={20} color={colors.primary} fontWeight="700" flexShrink={0}>›</Text>
                </XStack>
              ))}
            </YStack>
          );
        })}

        {/* TapPay 商店審核揭露：公司／統編／聯繫方式／退換貨政策／參考售價 */}
        <MerchantInfo />

        {/* 唯一的「真正離開練習模式」出口（其餘路徑都回這個選單） */}
        <XStack
          mt={24} ai="center" jc="center" borderRadius={14} py={13} borderWidth={1} borderColor={colors.border || "#E7ECF3"} bg="#fff" cursor="pointer"
          pressStyle={{ opacity: 0.7 }}
          onPress={() => { exitPractice(); navigate("/", { replace: true }); }}>
          <Text fontSize={14} fontWeight="700" color={colors.gray}>離開練習模式</Text>
        </XStack>
      </ScrollView>
    </View>
  );
};

export default PracticeEntry;
